<script>
  // src/Mapbox.svelte
  /*____________ Imports ____________*/
  import mapboxgl from 'mapbox-gl';
  import 'mapbox-gl/dist/mapbox-gl.css';
  
  // Mobile detection
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  import { onMount, onDestroy } from "svelte";
  
  export let mapComponent;
  import MapScripting from "./MapScripting.svelte";
  import CampusMarker from "./CampusMarker.svelte";

  /*____________ Variables ____________*/
  export let tour;
  export let expanded;
  export let zoom;
  const { scenes } = tour;
  let hasMarkers = false;
  const markers = tour?.mapbox?.markers;
  
  let mapContainer;
  let mapInstance = null;

  $: if (expanded !== null && mapInstance) {
    mapInstance.resize();
    // don't show markers on mini map
    hasMarkers = markers && markers.length > 0 && expanded;
  }

  // Keep runtime map zoom in sync with `zoom` prop.
  // Previously (via @beyonk/svelte-mapbox) the <Map {zoom}/> prop was reactive;
  // with raw mapbox-gl we need to apply it explicitly when the parent toggles
  // between mini/expanded views.
  $: if (mapInstance && typeof zoom === "number" && !Number.isNaN(zoom)) {
    const currentZoom = mapInstance.getZoom();
    // Avoid spamming camera updates for tiny float differences.
    if (Math.abs(currentZoom - zoom) > 0.001) {
      mapInstance.jumpTo({ zoom });
    }
  }

  function debugRecentre(e) {
    console.log("event:", e);
    console.log("center:", e.center);
  }

  // Get map options based on device type
  function getMapOptions() {
    if (isMobile && tour?.mapbox?.mobile) {
      console.log("mobile options");
      return {
        pitch: tour?.mapbox?.mobile?.mapPitch ?? tour?.mapbox?.mapPitch,
        bearing: tour?.mapbox?.mobile?.mapBearing ?? tour?.mapbox?.mapBearing,
        center: tour?.mapbox?.mobile?.mapCenter ?? tour?.mapbox?.mapCenter,
        respectPrefersReducedMotion: true,
      };
    }
    return {
      pitch: tour?.mapbox?.mapPitch,
      bearing: tour?.mapbox?.mapBearing,
      center: tour?.mapbox?.mapCenter,
      respectPrefersReducedMotion: true,
    };
  }

  let interval;
  onMount(() => {
    if (!mapContainer || !tour?.mapbox?.mapboxAccessToken) {
      console.error("Map container or access token missing");
      return;
    }

    // Set access token
    mapboxgl.accessToken = tour.mapbox.mapboxAccessToken;

    // Initialize map
    const mapOptions = {
      container: mapContainer,
      style: tour.mapbox.mapboxStyleUrl,
      zoom: zoom,
      ...getMapOptions()
    };

    mapInstance = new mapboxgl.Map(mapOptions);
    mapComponent = mapInstance; // Bind for parent component

    // Add controls for mobile
    if (isMobile) {
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      });
      mapInstance.addControl(geolocateControl, 'top-right');
    }

    // Handle map events
    mapInstance.on('moveend', () => {
      debugRecentre({ center: mapInstance.getCenter() });
    });

    // Hide controls from keyboard navigation
    interval = setInterval(() => {
      const container = document.querySelector('.mapboxgl-control-container');
      if (container) {
        const controls = container.querySelectorAll('button, a, input');
        controls.forEach(control => {
          control.tabIndex = -1;
          control.setAttribute('aria-hidden', 'true');
        });
      }
    }, 100);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  });

  onDestroy(() => {
    if (interval) {
      clearInterval(interval);
    }
    if (mapInstance) {
      mapInstance.remove();
      mapInstance = null;
    }
  });
</script>

<div 
  class="map" 
  bind:this={mapContainer}
  role="button"
  tabindex="0"
  on:click={() => {
    if (!expanded && window.CC?.switchView) {
      window.CC.switchView();
    }
  }}
  on:keydown={(e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !expanded && window.CC?.switchView) {
      e.preventDefault();
      window.CC.switchView();
    }
  }}
>
  <!-- Markers -->
  {#if hasMarkers && mapInstance}
    {#each markers as marker}
      <CampusMarker {...marker} map={mapInstance} />
    {/each}
  {/if}
  <!-- End Markers -->

  {#if mapInstance}
    <MapScripting
      {tour}
      {expanded}
      map={mapInstance}
    />
  {/if}
</div>

<style>
  :global(.map canvas) {
    width: 100% !important;
    height: 100% !important;
  }

  :global(.mapboxgl-ctrl-top-right) {
    top: 65px !important;
    right: 18px !important;
  }
</style>
